import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

// Shared accordion polish — soft border, rounded corners, subtle hover lift.
// Drops the default MUI separator line (`:before`) and zero-margin override
// for the expanded state so a clean ring of border stays around each card.
const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  boxShadow: 1,
  backgroundColor: 'background.paper',
  transition: 'box-shadow 200ms ease, border-color 200ms ease',
  '&:hover': {
    boxShadow: 3,
    borderColor: 'rgba(31, 77, 131, 0.4)',
  },
  // Expanded state has higher specificity, so it has to re-state the
  // bottom margin or our spacing collapses when accordions open.
  '&.Mui-expanded': { margin: 0, marginBottom: '24px' },
  '&:before': { display: 'none' },
  marginBottom: '24px',
  '&:last-of-type': {
    marginBottom: 0,
    '&.Mui-expanded': { marginBottom: 0 },
  },
};

const accordionSummarySx = {
  '&:hover': { bgcolor: 'action.hover' },
  '& .MuiAccordionSummary-content': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
};

/**
 * A collapsible section card with a polished accordion header that carries
 * two controls: the expand/collapse chevron and an expand-to-fullscreen
 * button that grows the body to (near) full viewport height.
 *
 * Collapse state is controlled-or-uncontrolled: pass `expanded` +
 * `onExpandedChange` to drive it externally, otherwise it manages its own
 * state seeded from `defaultExpanded`.
 *
 * `children` may be a node, or a render function `(isFullscreen) => node`
 * so consumers can vary content (e.g. page size) by the fullscreen state.
 */
const ExpandableSection = ({
  title,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
  collapsedHeight = 'auto',
  expandedHeight = 'calc(90vh - 100px)',
  children,
}) => {
  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const open = isControlled ? expanded : internalExpanded;

  const [fullscreen, setFullscreen] = useState(false);

  const handleChange = (_event, isExpanded) => {
    if (!isControlled) setInternalExpanded(isExpanded);
    onExpandedChange?.(_event, isExpanded);
  };

  const toggleFullscreen = () => setFullscreen((prev) => !prev);

  return (
    <Accordion expanded={open} onChange={handleChange} sx={accordionSx}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
        <Typography
          variant="h5"
          sx={{ marginLeft: '31px', fontSize: '1.55rem' }}
        >
          {title}
        </Typography>
        <Tooltip
          title={
            fullscreen ? `Minimize ${title}` : `Expand ${title} to full view`
          }
        >
          <IconButton
            component="div"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                toggleFullscreen();
              }
            }}
            size="small"
            aria-label={
              fullscreen
                ? `Minimize ${title} view`
                : `Expand ${title} to full view`
            }
            sx={{ ml: 'auto', mr: 1, cursor: 'pointer' }}
          >
            {fullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
        </Tooltip>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            height: fullscreen ? expandedHeight : collapsedHeight,
            transition: 'height 300ms ease-in-out',
          }}
        >
          {typeof children === 'function' ? children(fullscreen) : children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ExpandableSection;
